import React, { useEffect, useState } from "react";
import { ICartItem, IOnlineMenuItem } from "@dining/api-client";
import {
  ICartItemFormValues,
  cartItemFormSchema,
  createCartItem,
  createInitialValues,
} from "./Schema";
import { Form, Formik } from "formik";
import { useStyles } from "./Styles";
import { Body1, Button, Subtitle2, mergeClasses } from "@fluentui/react-components";
import { DisplayItemAmount } from "./DisplayItemAmount";
import { CartItemOptionGroups } from "./OptionGroup";
import { useAnnounce } from "@fluentui/react-components";

type CartItemFormProps = {
  item: IOnlineMenuItem;
  onSubmit: (value: ICartItem) => Promise<void>;
  editId?: string | null;
  isAvailable?: boolean;
  customFoodInstructions?: string;
};

export const CartItemForm = ({
  item,
  onSubmit,
  editId,
  isAvailable,
  customFoodInstructions,
}: CartItemFormProps) => {
  const styles = useStyles();
  const { announce } = useAnnounce();
  const initialValues = createInitialValues(item);
  const [amount] = useState<number>(item.pricing?.amount || item.amount || 0);
  const [amountWithOptions, setAmountWithOptions] = useState<number>(amount);
  const [currencyUnit] = useState<string>(item.pricing?.currencyUnit || "");
  const [customInstructions, setCustomInstructions] = useState<string | undefined>("");

  const submitHandler: (values: ICartItemFormValues) => Promise<void> = async (
    values: ICartItemFormValues,
  ) => {
    if (editId) {
      values["customInstructions"] = customInstructions;
    }
    const result: ICartItem = createCartItem(values, item, amountWithOptions);
    announce("Added to cart.");
    await onSubmit(result);
  };

  useEffect(() => {
    if (editId) {
      setCustomInstructions(customFoodInstructions);
    }
  }, [editId, customFoodInstructions]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submitHandler}
      validationSchema={cartItemFormSchema}
      validateOnMount
    >
      {({ handleSubmit, isSubmitting, setFieldValue }) => {
        const isCharacterLimitCrossed = customInstructions && customInstructions.length >= 250;

        return (
          <Form className={styles.root} data-testid="cartItemForm" onSubmit={handleSubmit}>
            <CartItemOptionGroups isMenuItemAvailableOnline={isAvailable ? true : false} />

            {isAvailable && (
              <>
                <div className={styles.customInstructionsContainer}>
                  <label
                    id="customInstructionsLabel"
                    htmlFor="customInstructionsTextarea"
                    className={styles.customInstructionsText}
                  >
                    Add custom instructions:
                  </label>
                  <Body1 className={styles.maxCharactersText}>(Max 250 characters)</Body1>
                </div>

                <textarea
                  id="customInstructionsTextarea"
                  className={
                    isCharacterLimitCrossed ? styles.overCharacterLimit : styles.customInstructions
                  }
                  placeholder="Include any meal preparation notes or custom instructions here (e.g. less spicy, sauce on the side)"
                  aria-labelledby="customInstructionsLabel"
                  value={customInstructions}
                  onChange={(e) => {
                    setFieldValue("customInstructions", e.target.value);
                    setCustomInstructions(e.target.value);
                  }}
                  maxLength={250}
                />

                {isCharacterLimitCrossed && (
                  <Body1 className={styles.errorMessage}>
                    250-character limit exceeded. Please shorten text and try again.
                  </Body1>
                )}
              </>
            )}

            {item.vendorId !== "Microsoft" && (
              <div
                className={mergeClasses(
                  styles.footer,
                  isAvailable ? styles.backgroundHoverColor : "",
                )}
              >
                <Button
                  type="submit"
                  appearance="primary"
                  data-testid="addToOrderButton"
                  disabled={isSubmitting || !isAvailable}
                >
                  <Subtitle2>{editId ? "Update" : "Add to order"} &sdot;</Subtitle2>
                  <DisplayItemAmount
                    amount={amount}
                    amountWithOptions={amountWithOptions}
                    currencyUnit={currencyUnit}
                    setAmountWithOptions={setAmountWithOptions}
                  />
                </Button>
              </div>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};
